import {
  BadgeDollarSign, Boxes, BriefcaseBusiness, Building2, CalendarDays, ChefHat,
  CircleDollarSign, Clock3, CloudCog, CreditCard, FileClock, Layers3, Monitor,
  MonitorSmartphone, Package, PackageOpen, QrCode, Receipt, ReceiptText, ScanBarcode,
  ShieldCheck, Smartphone, Store, Tags, UserRoundCog, Users, UtensilsCrossed, WalletCards,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { ProductId } from '../types'

export type PlatformDetailId = 'products' | 'staff' | 'multi-store' | 'devices' | 'payments'

export interface CapabilityGroup {
  title: string
  description: string
  icon: LucideIcon
  items: { label: string; icon: LucideIcon }[]
}

export const platformGroups: CapabilityGroup[] = [
  {
    title: 'Business', description: 'The structure and people behind every store.', icon: Building2,
    items: [
      { label: 'Businesses', icon: BriefcaseBusiness }, { label: 'Stores', icon: Store },
      { label: 'Users', icon: Users }, { label: 'Staff', icon: UserRoundCog }, { label: 'Roles', icon: ShieldCheck },
    ],
  },
  {
    title: 'Commerce', description: 'Shared product information with store-specific operation.', icon: Package,
    items: [
      { label: 'Products', icon: Package }, { label: 'Product variants', icon: PackageOpen },
      { label: 'Units of measure', icon: Layers3 }, { label: 'Pricing', icon: Tags },
      { label: 'Stock', icon: Boxes }, { label: 'Orders', icon: ReceiptText },
    ],
  },
  {
    title: 'Daily operations', description: 'The context that makes each shift and device accountable.', icon: Clock3,
    items: [
      { label: 'Work Days', icon: CalendarDays }, { label: 'Staff Shifts', icon: Clock3 },
      { label: 'Cash Sessions', icon: WalletCards }, { label: 'Devices', icon: MonitorSmartphone },
    ],
  },
  {
    title: 'Financial workflow', description: 'Payment and receipt context connected to the original order.', icon: CreditCard,
    items: [
      { label: 'Payments', icon: CreditCard }, { label: 'Receipts', icon: Receipt },
      { label: 'Fiscal operations', icon: BadgeDollarSign }, { label: 'Operational history', icon: FileClock },
    ],
  },
]

export const platformApplications: { id: ProductId; role: string }[] = [
  { id: 'serve', role: 'Hospitality workstation' }, { id: 'pos', role: 'Retail checkout' },
  { id: 'kds', role: 'Kitchen and bar preparation' }, { id: 'waiter', role: 'Mobile floor service' },
  { id: 'connect', role: 'Customer QR experience' },
]

export interface PlatformDetailContent {
  id: PlatformDetailId
  eyebrow: string
  hero: string
  lead: string
  icon: LucideIcon
  groups: { title: string; description: string; items: string[] }[]
  callout: { title: string; description: string }
}

export const platformDetails: Record<PlatformDetailId, PlatformDetailContent> = {
  products: {
    id: 'products', eyebrow: 'Products and inventory', hero: 'Central catalog. Local operation.', icon: Package,
    lead: 'Keep shared product information organized at business level while each store maintains the stock, cost, pricing, and assignments relevant to its operation.',
    groups: [
      { title: 'Shared catalog', description: 'Build a coherent product foundation for the business.', items: ['Products', 'Product variants', 'Units of measure', 'Product groups', 'Recipes where applicable'] },
      { title: 'Store context', description: 'Connect catalog information to the store where it is used.', items: ['Store assignments', 'Store-level inventory', 'Store-level cost', 'Store pricing', 'Availability'] },
      { title: 'Supply context', description: 'Keep operational product information organized where applicable.', items: ['Suppliers where applicable', 'Stock movement context', 'Pricing history', 'Operational history'] },
    ],
    callout: { title: 'Shared information without flattening every store.', description: 'A central catalog keeps product identity consistent while store-specific stock and operating context remain local.' },
  },
  staff: {
    id: 'staff', eyebrow: 'Staff and permissions', hero: 'The right tools for every role.', icon: Users,
    lead: 'Magacin connects individual staff identity, store responsibility, and role-appropriate controls without putting every action in front of every person.',
    groups: [
      { title: 'Business roles', description: 'Oversight roles for the wider business context.', items: ['Owner', 'Manager', 'Accountant'] },
      { title: 'Store roles', description: 'Operational roles focused on the work happening now.', items: ['Store manager', 'Cashier', 'Waiter', 'Kitchen', 'Bar'] },
      { title: 'Operational permissions', description: 'Sensitive actions stay connected to staff responsibility.', items: ['Take payment', 'Apply discount', 'Cancel sent items', 'Void order', 'Refund payment', 'Manage shift', 'View reports'] },
    ],
    callout: { title: 'Personal access. Fast staff switching.', description: 'Restaurant staff can use a personal PIN while the configured store device remains ready for the next authorized team member.' },
  },
  'multi-store': {
    id: 'multi-store', eyebrow: 'Multi-store architecture', hero: 'One business. Multiple stores.', icon: Store,
    lead: 'Organize shared business information centrally while each location keeps its own staff, devices, stock, and operational setup focused.',
    groups: [
      { title: 'Business level', description: 'The common structure behind the locations.', items: ['Business identity', 'Shared catalog', 'Users', 'Store organization', 'Subscription context'] },
      { title: 'Store level', description: 'The operational context that belongs to one location.', items: ['Staff assignments', 'Devices', 'Stock', 'Pricing context', 'Store module'] },
      { title: 'Flexible setup', description: 'The application mix can follow the work at each location.', items: ['Hospitality location', 'Retail location', 'Kitchen or bar stations', 'Mobile staff', 'Customer QR experience'] },
    ],
    callout: { title: 'Each store stays operationally focused.', description: 'A restaurant location can use Serve, KDS, Waiter, and Connect while a retail location uses POS under the same business structure.' },
  },
  devices: {
    id: 'devices', eyebrow: 'Device ecosystem', hero: 'Connect once. Operate every day.', icon: MonitorSmartphone,
    lead: 'Each physical device belongs to the right store and application context, so the team can return to a validated operational workspace when the app starts.',
    groups: [
      { title: 'Workstations', description: 'Fixed operational interfaces for hospitality and retail.', items: ['Serve terminal', 'POS terminal', 'Windows workstation', 'Store assignment'] },
      { title: 'Operational displays', description: 'Purpose-built screens where preparation and service happen.', items: ['Kitchen KDS', 'Bar KDS', 'Preparation-station assignment', 'Active device state'] },
      { title: 'Mobile and customer', description: 'Interfaces that move with staff or open directly for guests.', items: ['Waiter mobile device', 'Connect customer browser', 'QR table context', 'Application type'] },
    ],
    callout: { title: 'Install → connect → select store → validate → sign in.', description: 'The configured physical device can be recognized when the application launches, while staff still use their own authorized access.' },
  },
  payments: {
    id: 'payments', eyebrow: 'Payments and receipts', hero: 'Payment stays part of the order.', icon: CircleDollarSign,
    lead: 'Magacin keeps totals, payment state, payment history, staff context, and receipts connected to the operation that created them.',
    groups: [
      { title: 'Payment flow', description: 'Keep payment progress clear through completion.', items: ['Cash', 'Payment methods', 'Partial payment', 'Remaining balance', 'Payment history'] },
      { title: 'Payment states', description: 'Understand the financial state without relying on totals alone.', items: ['Unpaid', 'Partially paid', 'Paid', 'Partially refunded', 'Refunded', 'Failed'] },
      { title: 'Receipts and fiscal workflow', description: 'Keep receipts tied to the order lifecycle.', items: ['Receipt generation', 'Order-linked receipts', 'Credit notes', 'Failed fiscal retry', 'Fiscal cancellation where supported'] },
    ],
    callout: { title: 'Fiscalization support is provider and market dependent.', description: 'Availability and workflow depend on the configured fiscal provider and operating market; no country coverage is implied.' },
  },
}

export const deviceCatalog = [
  { title: 'Serve terminal', description: 'Windows restaurant workstation', icon: UtensilsCrossed },
  { title: 'POS terminal', description: 'Windows retail workstation', icon: ScanBarcode },
  { title: 'Kitchen / bar KDS', description: 'Preparation display', icon: ChefHat },
  { title: 'Waiter device', description: 'Mobile staff application', icon: Smartphone },
  { title: 'Connect experience', description: 'Customer browser via QR', icon: QrCode },
]

export const subscriptionApplications = [
  { label: 'POS', icon: ScanBarcode }, { label: 'Serve', icon: UtensilsCrossed },
  { label: 'KDS', icon: ChefHat }, { label: 'Waiter Mobile', icon: Smartphone },
  { label: 'Customer PWA / Connect', icon: QrCode },
]

export const subscriptionStates = ['Active', 'Suspended', 'Cancelled', 'Expired']

export const portalNavigation = [
  { label: 'Overview', href: '/platform', icon: Layers3, description: 'How the ecosystem fits together' },
  { label: 'Business management', href: '/business', icon: Building2, description: 'Businesses, stores, and central control' },
  { label: 'Products & stock', href: '/platform/products', icon: Package, description: 'Catalog, pricing, and inventory' },
  { label: 'Staff & permissions', href: '/platform/staff', icon: Users, description: 'Roles and operational access' },
  { label: 'Multi-store', href: '/platform/multi-store', icon: Store, description: 'Shared business, local operation' },
  { label: 'Devices', href: '/platform/devices', icon: MonitorSmartphone, description: 'Connected workstations and apps' },
  { label: 'Payments', href: '/platform/payments', icon: CreditCard, description: 'Payments, receipts, and state' },
  { label: 'Subscriptions', href: '/subscriptions', icon: CloudCog, description: 'Application assignment and periods' },
]

export const deviceSetupSteps = [
  { label: 'Install app', icon: Monitor }, { label: 'Connect device', icon: MonitorSmartphone },
  { label: 'Select store', icon: Store }, { label: 'Device is validated', icon: ShieldCheck },
  { label: 'Staff signs in', icon: Users }, { label: 'Start operating', icon: CircleDollarSign },
]
